// ============================================================================
// main.js — TITANLORD bootstrap. Builds the world + all render/UI systems and
// the GameManager (menu → briefing → battle → results). The frame loop runs
// fixed-timestep logic ONLY during a battle (GameManager decides), and always
// renders the interpolated scene behind whatever meta-screen is showing.
// ============================================================================

import { World } from './world/world.js';
import { FixedClock } from './core/clock.js';
import { Sim } from './core/sim.js';
import { TICK_DT } from './core/constants.js';

import { Renderer } from './render/renderer.js';
import { FollowCamera } from './render/camera.js';
import { CrowdRenderer } from './render/crowd.js';
import { BlobShadows } from './render/blobShadow.js';
import { SwordTrail } from './render/trail.js';
import { PlayerRender } from './render/playerRender.js';
import { WarlordRenderer } from './render/warlordRender.js';
import { FX } from './render/fx.js';

import { Battlefield } from './world/battlefield.js';
import { initMorale } from './world/morale.js';

import { Input } from './input/input.js';
import { AudioEngine } from './audio/audio.js';
import { HUD } from './ui/hud.js';
import { Minimap } from './ui/minimap.js';
import { Banners } from './ui/banners.js';
import { Barks } from './ui/barks.js';
import { DebugOverlay } from './ui/debug.js';
import { Screens } from './ui/screens.js';
import { WorldMap } from './ui/worldmap.js';
import { GameManager } from './game/game.js';

const appEl = document.getElementById('app');
const hudEl = document.getElementById('hud');
// retire the old single-battle boot overlay — the menu is the entry point now
const bootEl = document.getElementById('boot');
if (bootEl) bootEl.remove();

// ---- world & systems ----
const world = new World((Math.random() * 1e9) | 0);
const renderer = new Renderer(appEl);
const followCam = new FollowCamera(renderer.camera);
const crowd = new CrowdRenderer(renderer.scene);
const blobs = new BlobShadows(renderer.scene);
const trail = new SwordTrail(renderer.scene);
const playerRender = new PlayerRender(renderer.scene, trail);
const warlordRender = new WarlordRenderer(renderer.scene);
const fx = new FX(renderer.scene, renderer.camera);

const battlefield = new Battlefield(renderer, world);
world.battlefield = battlefield;
initMorale(world);

const input = new Input(renderer.renderer.domElement);
const audio = new AudioEngine();
audio.init(world);
const hud = new HUD(hudEl, world);
const minimap = new Minimap(hudEl, world);
const banners = new Banners(hudEl, world);
const barks = new Barks(hudEl, world);
const debug = new DebugOverlay(hudEl);
const screens = new Screens(hudEl);
const worldmap = new WorldMap(hudEl);

const sim = new Sim(world);
const gm = new GameManager({
  world, sim, screens, worldmap, battlefield, followCam, audio, input,
  renderer, fx, crowd,   // for biome theming + the Conquest layer
});
gm.gotoMenu();

// ---- frame loop ----
const clock = new FixedClock(TICK_DT);
const stats = { logicMs: 0, renderMs: 0, steps: 0, drawCalls: 0, tris: 0 };

function frame(nowMs) {
  const now = nowMs / 1000;
  const steps = clock.update(now);
  const frameDt = clock.frameDt;

  input.beginFrame(followCam);

  // fixed-step logic ONLY during an active battle (hit-stop freezes the sim)
  const t0 = performance.now();
  let ran = 0;
  if (gm.isBattle()) {
    for (let s = 0; s < steps; s++) {
      if (world.hitstop > 0) { world.hitstop -= TICK_DT; continue; }
      sim.step(input);
      ran++;
    }
    gm.checkOutcome();
  }
  stats.logicMs = performance.now() - t0;
  stats.steps = ran;

  const alpha = gm.isBattle() ? clock.alpha : 1;
  const timeSec = world.simTime;

  // render-side systems (always, so the scene sits behind the meta-screens)
  followCam.update(world.player, input, frameDt, world);
  crowd.update(world, alpha, timeSec);
  warlordRender.update(world, alpha, frameDt);
  blobs.update(world, alpha);
  playerRender.update(world, alpha, frameDt);
  fx.update(world, frameDt);
  battlefield.update(frameDt, timeSec);

  const r0 = performance.now();
  renderer.render();
  stats.renderMs = performance.now() - r0;
  stats.drawCalls = renderer.drawCalls;
  stats.tris = renderer.triangles;

  hud.update(world, renderer.camera, frameDt);
  minimap.update(world, frameDt);
  banners.update(world, frameDt);
  barks.update(world, frameDt);
  if (gm.isBattle()) screens.updateObjHud(world);
  debug.update(world, stats, frameDt);

  input.endFrame();
  requestAnimationFrame(frame);
}
requestAnimationFrame(frame);

window.TITANLORD = { world, renderer, stats, sim, input, followCam, gm };
